// Persistência leve (localStorage) para tornar a reentrada após QUEDA
// transparente: o usuário volta para a MESMA sala, sem precisar "pedir para
// entrar" de novo, e com o avatar na MESMA posição em que estava.
//
// Dois conceitos separados, ambos por sala + usuário:
//  - Intenção de reentrada (rejoin intent): "este usuário estava ativo nesta
//    sala agora há pouco". Tem TTL curto para não reentrar em sessões antigas.
//  - Última posição do avatar: usada como fallback quando o doc do participante
//    foi recriado do zero (ex.: limpeza do monitor) e perdeu xRel/yRel.

const INTENT_PREFIX = "rtk_rejoin_intent_";
const POSITION_PREFIX = "rtk_rejoin_pos_";

// Janela em que consideramos a reentrada como "recuperação de queda".
// Mantida ACIMA do grace do monitor de salas (que marca o participante inativo e
// só apaga o doc após ~10 min — inactiveGracePeriodMs em functions/src/dyte.ts).
// Assim, mesmo numa queda longa que chega a recriar o doc do participante, o
// reentrar automático ainda é tratado como recuperação e restaura avatar/posição,
// em vez de cair no fluxo de "primeira entrada" (knock + posição aleatória).
const INTENT_TTL_MS = 15 * 60 * 1000; // 15 minutos (> 10 min do grace do monitor)
// A posição é só uma dica visual; pode durar mais.
const POSITION_TTL_MS = 60 * 60 * 1000; // 60 minutos

export type RejoinIntent = {
    roomId: string;
    userId: string;
    selectedAvatarId: string | null;
    sessionIdentityKey: string | null;
    ts: number;
};

export type SavedAvatarPosition = {
    xRel: number;
    yRel: number;
    pose?: string | null;
    direction?: string | null;
    avatarId?: string | null;
    ts: number;
};

// ---------- Intenção de reentrada ----------

export const markRejoinIntent = (
    roomId: string,
    userId: string,
    selectedAvatarId: string | null,
    sessionIdentityKey: string | null,
): void => {
    if (!roomId || !userId) return;
    try {
        const payload: RejoinIntent = {
            roomId,
            userId,
            selectedAvatarId: selectedAvatarId ?? null,
            sessionIdentityKey: sessionIdentityKey ?? null,
            ts: Date.now(),
        };
        window.localStorage.setItem(INTENT_PREFIX + roomId, JSON.stringify(payload));
    } catch {
        // storage bloqueado: seguimos sem o recurso, sem quebrar o fluxo.
    }
};

export const readRejoinIntent = (roomId: string, userId: string): RejoinIntent | null => {
    if (!roomId || !userId) return null;
    try {
        const raw = window.localStorage.getItem(INTENT_PREFIX + roomId);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as RejoinIntent;
        if (parsed.userId !== userId) return null;
        if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > INTENT_TTL_MS) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const clearRejoinIntent = (roomId: string): void => {
    if (!roomId) return;
    try {
        window.localStorage.removeItem(INTENT_PREFIX + roomId);
    } catch {
        // noop
    }
};

// ---------- Última posição do avatar ----------

export const saveAvatarPosition = (
    roomId: string,
    userId: string,
    position: Omit<SavedAvatarPosition, "ts">,
): void => {
    if (!roomId || !userId) return;
    if (typeof position.xRel !== "number" || typeof position.yRel !== "number") return;
    try {
        const payload: SavedAvatarPosition = { ...position, ts: Date.now() };
        window.localStorage.setItem(POSITION_PREFIX + roomId + "_" + userId, JSON.stringify(payload));
    } catch {
        // noop
    }
};

export const readAvatarPosition = (roomId: string, userId: string): SavedAvatarPosition | null => {
    if (!roomId || !userId) return null;
    try {
        const raw = window.localStorage.getItem(POSITION_PREFIX + roomId + "_" + userId);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SavedAvatarPosition;
        if (
            typeof parsed.xRel !== "number" ||
            typeof parsed.yRel !== "number" ||
            typeof parsed.ts !== "number" ||
            Date.now() - parsed.ts > POSITION_TTL_MS
        ) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};
